import type { ApplicationWithStats } from '@owl/shared';
import { useCallback, useState } from 'react';
import { api } from '../../../lib/api';
import { type ApiHealth, pingApiHealth } from '../../../lib/apiHealth';
import { aggregateAppsHealth } from '../../../lib/appHealth';
import { useLoader } from '../../../lib/useLoader';
import { PushToggle } from '../../PushToggle';
import { Icon } from '../../icons';
import { OwlLogo } from '../../shared';
import type { MobileShellCtx } from '../MobileShellCtx';

export function SettingsPage({ ctx }: { ctx: MobileShellCtx }) {
  const [settings, setSettings] = useState<Record<string, unknown> | null>(null);
  const [apiState, setApiState] = useState<ApiHealth>('checking');
  const [latency, setLatency] = useState<number | null>(null);
  const [appsSummary, setAppsSummary] = useState<{
    total: number;
    worst: 'ok' | 'warn' | 'down';
    downCount: number;
    warnCount: number;
  }>({ total: 0, worst: 'ok', downCount: 0, warnCount: 0 });
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (isCancelled: () => boolean) => {
    try {
      const [s, ping, apps] = await Promise.all([
        api.getSettings().catch((): { settings: Record<string, unknown> } => ({ settings: {} })),
        pingApiHealth(),
        api
          .listApplications()
          .then((r) => r.items)
          .catch(() => [] as ApplicationWithStats[]),
      ]);
      if (isCancelled()) return;
      setSettings(s.settings ?? {});
      setApiState(ping.state);
      setLatency(ping.latencyMs);
      const h = aggregateAppsHealth(apps);
      setAppsSummary({
        total: apps.length,
        worst: h.worst === 'idle' ? 'ok' : h.worst,
        downCount: h.downCount,
        warnCount: h.warnCount,
      });
    } catch (e) {
      if (!isCancelled()) setErr((e as Error).message);
    }
  }, []);

  useLoader(load, ctx.refreshTick);

  const themeName =
    settings && typeof settings.theme === 'object' && settings.theme !== null
      ? String((settings.theme as Record<string, unknown>).palette ?? '—')
      : '—';
  const fontSans =
    settings && typeof settings.theme === 'object' && settings.theme !== null
      ? String(
          ((settings.theme as Record<string, unknown>).fonts as Record<string, unknown> | undefined)
            ?.sans ?? '—',
        )
      : '—';

  const initials = (ctx.user.email.slice(0, 2) || 'OW').toUpperCase();

  const apiSub =
    apiState === 'checking'
      ? 'Comprobando…'
      : apiState === 'down'
        ? 'Ingest caído'
        : `Ingest ${apiState} · ${latency?.toFixed(0) ?? '—'}ms`;
  const apiColor =
    apiState === 'ok'
      ? '#92d3a3'
      : apiState === 'slow'
        ? 'var(--accent)'
        : apiState === 'down'
          ? '#e0736f'
          : 'var(--fg-3)';

  return (
    <div className="m-page">
      {err && <div className="m-error-banner">{err}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'var(--bg-2)',
              border: '1px solid var(--border-2)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <OwlLogo size={28} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>OWL</div>
            <div
              className="mono"
              style={{ fontSize: 11, color: 'var(--fg-4)', letterSpacing: '0.16em' }}
            >
              v{__APP_VERSION__} · {ctx.environment}
            </div>
          </div>
        </div>
      </div>

      <div className="m-section-h">
        <span className="m-section-t">Estado</span>
      </div>
      <section className="set-list">
        <div className="set-row" style={{ cursor: 'default' }}>
          <div className="ico">
            <Icon.Server size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl">API</div>
            <div className="sub" style={{ color: apiColor }}>
              ● {apiSub}
            </div>
          </div>
        </div>
        <div className="set-row" style={{ cursor: 'default' }}>
          <div className="ico">
            <Icon.Activity size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl">Aplicaciones</div>
            <div className="sub">
              {appsSummary.total} apps · {appsSummary.downCount} down · {appsSummary.warnCount} warn
            </div>
          </div>
          <span className={`m-health-pill ${appsSummary.worst}`}>{appsSummary.worst}</span>
        </div>
        <div className="set-row" style={{ cursor: 'default' }}>
          <div className="ico">
            <Icon.Spark size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl">Live stream</div>
            <div className="sub">
              {ctx.liveConnected ? 'Conectado' : 'Sin conexión'} · {ctx.unreadAlerts} alertas
              pendientes
            </div>
          </div>
          <span className={`m-health-pill ${ctx.liveConnected ? 'ok' : 'down'}`}>
            {ctx.liveConnected ? 'live' : 'off'}
          </span>
        </div>
      </section>

      <div className="m-section-h">
        <span className="m-section-t">Notificaciones</span>
      </div>
      <section className="set-list">
        <div className="set-row" style={{ cursor: 'default', alignItems: 'flex-start' }}>
          <div className="ico">
            <Icon.Bell size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <PushToggle />
          </div>
        </div>
      </section>

      <div className="m-section-h">
        <span className="m-section-t">Cuenta</span>
      </div>
      <section className="set-list">
        <div className="set-row" style={{ cursor: 'default' }}>
          <div
            className="ico"
            style={{
              background: 'linear-gradient(135deg, #2A2A30, #161618)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.04em',
            }}
          >
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl">{ctx.user.email}</div>
            <div className="sub">{ctx.user.role}</div>
          </div>
        </div>
        <div className="set-row" style={{ cursor: 'default' }}>
          <div className="ico">
            <Icon.Palette size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl">Apariencia</div>
            <div className="sub">Dark · paleta {themeName}</div>
          </div>
        </div>
      </section>

      <div className="m-section-h">
        <span className="m-section-t">Preferencias (read-only)</span>
      </div>
      <section className="set-list">
        <div className="set-row" style={{ cursor: 'default' }}>
          <div className="ico">
            <Icon.Type size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl">Tipografía</div>
            <div className="sub">{fontSans}</div>
          </div>
        </div>
        <div className="set-row" style={{ cursor: 'default' }}>
          <div className="ico">
            <Icon.Clock size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl">Zona horaria</div>
            <div className="sub">Europe/Madrid</div>
          </div>
        </div>
        <div className="set-row" style={{ cursor: 'default' }}>
          <div className="ico">
            <Icon.Coin size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl">OWL Mobile</div>
            <div className="sub mono">v{__APP_VERSION__}</div>
          </div>
        </div>
      </section>

      <div className="m-section-h">
        <span className="m-section-t">Sesión</span>
      </div>
      <section className="set-list">
        <button type="button" className="set-row" onClick={ctx.onLogout}>
          <div className="ico" style={{ color: '#e0736f' }}>
            <Icon.LogOut size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="lbl" style={{ color: '#e0736f' }}>
              Cerrar sesión
            </div>
            <div className="sub">{ctx.user.email}</div>
          </div>
          <Icon.ChevronRight size={14} className="chev" />
        </button>
      </section>

      <div
        style={{
          fontSize: 11,
          color: 'var(--fg-4)',
          textAlign: 'center',
          marginTop: 14,
          padding: '0 12px',
        }}
      >
        Para configurar la app (apps, alertas, AI tokens, precios, etc.), entra desde la versión de
        escritorio.
      </div>

      <div style={{ padding: '18px 4px 6px', textAlign: 'center' }}>
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--fg-4)',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}
        >
          Observe · Warn · Log
        </div>
      </div>

      <div style={{ height: 8 }} />
    </div>
  );
}
